import { HasVariableScope } from '../bos/has-variable-scope.js'
import { JsonMappable } from '../data/json-mappable.js'

import type {
	HtmlType,
	TsCodeType,
	Class,
	EventType,
	BusinessObjectItem,
	NameAndType,
} from '../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { EventHandler } from './events/event-handler.js'
import { ScreenEditorInput } from './decorators/screen-editor-input.js'
import { BoVisitor } from '../bos/bo-visitor.js'
import { EditorPropertiesManager } from './decorators/editor-properties-manager.js'
import { BoReference } from '../bos/bo-reference.js'
import { Parameter } from '../script/parameter.js'
import type { LogicBlock } from './logic/logic-block.js'
import type { LayoutBlock } from './layout/layout-block.js'
import { BlockMenuEntry } from './block-menu-entry.js'
import type { IconBlockFontSet } from './other/icon-block.js'
import type { ContentAreaBlock } from './layout/content-area-block.js'
import { BlockStyle } from './styles/block-style.js'
import { Expression } from '../data/text-or-code.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'
import { getJsonTypeName } from '../util/data/get-json-type-name.js'
import { FormValidationRule } from './form/form-validation-rule.js'
import { clone } from 'ramda'
import { BlockWithPrefixIcon } from './common/block-with-prefix-icon.js'
import { Screen } from '../bos/screen.js'
import { EditorPropertyWithKey } from './decorators/editor-property-types.js'
import { BlockComponentImport } from './block-component-import.js'

export type IconPreviewType = {
	type: 'icon'
	fontIcon: string
	fontSet: IconBlockFontSet
	textAfter?: string
}
export type HtmlObjectPreview = { type: 'html'; html: string }
export type PreviewType = HtmlType | IconPreviewType | HtmlObjectPreview

export const BlockAppearances = {
	fill: 'Filled (with background)',
	outline: 'Outlined',
} as const
export type BlockAppearance = keyof typeof BlockAppearances
export const BlockColors = {
	'': 'Basic',
	primary: 'Primary',
	accent: 'Accent',
	warn: 'Warn',
} as const
export type BlockColor = keyof typeof BlockColors

export const BlockBindingType = {
	binding: 'Bind to property',
	getterAndAction: 'Provider getter expression and action',
}
export type BlockBindingType = keyof typeof BlockBindingType

@jsonClass('Block')
export class Block
	implements JsonMappable, HasVariableScope, BusinessObjectItem
{
	static readonly listInEditor: boolean = true
	bindingCode?: Expression
	bindingType?: BlockBindingType
	@jsonArrayProperty(FormValidationRule)
	validationRules?: FormValidationRule[]

	@jsonProperty(String)
	exampleText?: string

	canAcceptVariableString: ((variable: NameAndType) => boolean) | null = null
	acceptVariableString:
		| ((variable: NameAndType, screen: Screen) => void)
		| null = null

	@jsonProperty(String)
	get __type(): string {
		return getJsonTypeName(this, 'Block')
	}
	set __type(_) {
		/*ignore*/
	}

	@jsonProperty(Number) id: number = 0
	@jsonArrayProperty(() => Block) children: Block[] = []
	@jsonArrayProperty(String) classes: string[] = []

	@ScreenEditorInput({
		inputType: 'input',
		order: 1001,
		label: 'Flex',
		contentType: 'text',
		elementType: 'input',
	})
	@jsonProperty(String)
	flex?: string

	@ScreenEditorInput({
		inputType: 'checkbox',
		order: 1002,
		label: 'Fill parent',
	})
	@jsonProperty(Boolean)
	fillParent: boolean = false

	@ScreenEditorInput({
		inputType: 'code',
		order: 1001,
		label: (blocks) => {
			const nlp = blocks[0].getNonLogicParent()
			return `Span across ${nlp.isLayoutBlock() && nlp.layoutDirection == 'grid-horizontal' ? 'column' : 'row'} width`
		},
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: false,
		isVisible(blocks, _properties, _bo) {
			const isLogicBlock = blocks[0].isLogicBlock()
			if (isLogicBlock) return false
			const nlp = blocks[0].getNonLogicParent()
			return (
				nlp?.isLayoutBlock() &&
				(nlp.layoutDirection == 'grid-horizontal' ||
					nlp.layoutDirection == 'grid-vertical')
			)
		},
	})
	@jsonProperty(Expression)
	layoutSpanCode = new Expression('1')

	@ScreenEditorInput({
		inputType: 'code',
		order: 1001,
		label: (blocks) => {
			const nlp = blocks[0].getNonLogicParent()
			return `Place at ${nlp.isLayoutBlock() && nlp.layoutDirection == 'grid-horizontal' ? 'column' : 'row'} number (optional)`
		},
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: false,
		isVisible(blocks, _properties, _bo) {
			const isLogicBlock = blocks[0].isLogicBlock()
			if (isLogicBlock) return false
			const nlp = blocks[0].getNonLogicParent()
			return (
				nlp?.isLayoutBlock() &&
				(nlp.layoutDirection == 'grid-horizontal' ||
					nlp.layoutDirection == 'grid-vertical')
			)
		},
	})
	@jsonProperty(Expression)
	layoutStartLineCode = new Expression('')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 100,
		label: 'Color',
		tab: 'Appearance',
		isVisible: (blocks) => blocks[0].canHaveColor(),
		options: Object.entries(BlockColors),
	})
	color: BlockColor = ''

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 100,
		label: 'Appearance',
		tab: 'Appearance',
		isVisible: (blocks) => blocks[0].canHaveAppearance(),
		options: Object.entries(BlockAppearances),
	})
	appearance?: BlockAppearance = 'fill'

	@jsonArrayProperty(BlockStyle)
	styles: BlockStyle[] = []
	@jsonArrayProperty(EventHandler)
	eventHandlers: EventHandler[] = []

	parent?: Block
	readonly allowedChildTypes: '*' | Class<Block>[] = '*'

	get allowedEventTypes(): EventType[] {
		return []
	}
	get allowedStylePrefixes(): string[] {
		return []
	}

	static createNewForEditor(): Block {
		return new this()
	}

	getEditorCategory(): string | null {
		return 'Others'
	}

	init(init?: object) {
		assignCommonProperties(this, init)
	}

	getAllowedChildTypes(): string[] | undefined {
		return undefined
	}

	canHaveChildren(): boolean {
		return true
	}

	canHaveChild(child: Block): boolean {
		if (this.allowedChildTypes == '*') return true

		const included = this.allowedChildTypes.includes(
			<Class<Block>>child.constructor,
		)
		return included
	}

	canHaveColor() {
		return false
	}

	canHaveAppearance() {
		return false
	}

	isLayoutBlock(): this is LayoutBlock {
		return false
	}

	isLogicBlock(): this is LogicBlock {
		return false
	}

	isBlockWithPrefixIcon(): this is BlockWithPrefixIcon {
		return false
	}

	getBlockName() {
		return this.__type.replace('Block', '').replace(/(?<!^)([A-Z])/g, ' $1')
	}

	getEditorTitle() {
		return this.getBlockName()
	}

	getPreviewDirection(): 'row' | 'column' {
		return 'column'
	}

	getPreviewStyles(): string {
		return `display: flex; flex-direction: ${this.getPreviewDirection()}; flex-wrap: wrap;`
	}

	getTitleColor() {
		return 'black'
	}

	getTitleBackgroundAndBorderColor() {
		return 'lightgray'
	}

	producePreview(): PreviewType {
		return ''
	}

	canHaveParent(_parent: Block | undefined): boolean {
		throw new Error("Shouldn't instantiate Block class directly")
	}

	needsBoSpecificDetailsForRefs(): BoReference[] {
		return []
	}

	protected transpileExpressionForHtml(tsCode: TsCodeType | undefined) {
		if (tsCode === undefined) return ''
		// TODO: get babel to work for transpiling backtick template literals
		return tsCode

		// const transpiled = transform(tsCode, {
		// 	plugins: ['@babel/plugin-transform-template-literals'],

		// })
		// return transpiled
	}

	public getOwnVariables(
		_includeDeclarationVariables: boolean,
	): Record<string, string | null> {
		return {}
	}

	public getOwnVariablesForChildContentArea(
		_contentArea: ContentAreaBlock,
	): Record<string, string | null> {
		return {}
	}

	getVariableScope(
		includeDeclarationVariables: boolean,
	): Record<string, string> {
		const parentScope = this.parent?.getVariableScope(
			includeDeclarationVariables,
		)
		const ownScope = this.getOwnVariables(includeDeclarationVariables)
		const scope = { ...parentScope, ...ownScope }

		for (const [name, type] of Object.entries(scope)) {
			if (type === null) {
				// allow unsetting variable (e.g. if a specific child ContentArea can't use the parent's variables) by setting type === null
				delete scope[name]
			}
		}

		return scope as Record<string, string>
	}

	getVariableScopeAsParameters(includeDeclarationVariables: boolean) {
		const scope = this.getVariableScope(includeDeclarationVariables)
		const params = Object.entries(scope).map(
			([name, type]) => new Parameter({ name, type }),
		)
		return params
	}

	calculateExampleText() {
		const obj = this as any
		const exampleText =
			obj.exampleText ||
			obj.bindingCode?.content
				?.replace(/.*\./g, '')
				?.replace(/((?<=[a-z0-9_])[A-Z])/g, ' $1')
				?.replace(/^[a-z]/, (char: string) => char.toUpperCase()) ||
			this.getEditorTitle()

		return exampleText
	}

	visit(
		visitor: (block: Block, parent: Block | null, idxInParent: number) => void,
		parent: Block | null,
		idxInParent: number,
		childrenOrder: 'first' | 'last' | 'ignore',
		childrenFn?: (block: Block) => Block[],
	) {
		const children = childrenFn?.(this) ?? this.children
		if (childrenOrder == 'first')
			children.forEach((child, idx) =>
				child.visit(visitor, this, idx, childrenOrder),
			)
		visitor(this, parent, idxInParent)
		if (childrenOrder == 'last')
			children.forEach((child, idx) =>
				child.visit(visitor, this, idx, childrenOrder),
			)
	}

	visitWithBoVisitor(visitor: BoVisitor, pathPrefix: (string | number)[]) {
		this.visitThisWithBoVisitor(visitor, pathPrefix)
		this.children.forEach((child, idx) =>
			child.visitWithBoVisitor(visitor, [...pathPrefix, idx]),
		)
	}

	protected visitThisWithBoVisitor(
		_visitor: BoVisitor,
		_pathPrefix: (string | number)[],
	) {}
	protected visitCodePropertyWithBoVisitor<T extends Block>(
		visitor: BoVisitor,
		pathPrefix: (string | number)[],
		blockClass: Class<T>,
		propertyKey: keyof T & string,
	) {
		const property = EditorPropertiesManager.getPropertyByKey(
			blockClass.prototype,
			propertyKey,
		)
		const blockAndProperty = { block: this, property }
		visitor.visitTsCode(
			Reflect.get(this, propertyKey) as string,
			blockAndProperty,
			'property',
			[...pathPrefix, propertyKey],
			(newCode) => Reflect.set(this, propertyKey, newCode),
		)
	}

	getBlockPath() {
		const indices: number[] = []
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		for (let block: Block = this; block.parent; block = block.parent) {
			const idx = block.parent?.children?.indexOf(block)
			if (idx != null) indices.push(idx)
		}

		return indices.reverse()
	}

	getEditableBo(): BoReference | null {
		return null
	}

	replaceBlock(oldBlock: Block, newBlock: Block, recursive: boolean) {
		let success = false

		this.visit(
			(block, parent, idxInParent) => {
				if (block === oldBlock) {
					if (parent?.children[idxInParent] === oldBlock) {
						parent.children[idxInParent] = newBlock
						success = true
					} else {
						console.error('Block could not be replaced; not found')
					}
				}
			},
			null,
			0,
			recursive ? 'first' : 'ignore',
		)

		return success
	}

	getNonLogicParent() {
		return this.getFirstParent((block) => !block.isLogicBlock())
	}

	getParentChain() {
		const parents: Block[] = []
		let block = this.parent
		while (block) {
			parents.push(block)
			block = block.parent
		}
		return parents
	}

	getFirstParent<T extends Block>(
		predicate: (block: Block) => block is T,
		startAtSelf?: boolean,
	): T
	getFirstParent(
		predicate: (block: Block) => boolean,
		startAtSelf?: boolean,
	): Block
	getFirstParent(predicate: (block: Block) => boolean, startAtSelf?: boolean) {
		let block = startAtSelf ? this : this.parent
		while (block) {
			if (predicate(block)) return block
			block = block.parent
		}
		return null
	}

	getMenuEntries(): BlockMenuEntry[] {
		return (
			[
				{
					label: 'Cut',
					onClick(editor) {
						editor.cut()
					},
				},
				{
					label: 'Copy',
					onClick(editor) {
						editor.copy()
					},
				},
				{
					label: 'Paste after',
					onClick(editor) {
						editor.paste('after', this)
					},
				},
				{
					label: 'Paste inside',
					onClick(editor) {
						editor.paste('inside', this)
					},
					isDisabled() {
						return !this.canHaveChildren()
					},
				},
				'---',
				{
					label: 'Delete',
					onClick(editor) {
						editor.delete()
					},
				},
				{
					label: 'Delete & keep children',
					onClick(editor) {
						editor.deleteKeepChildren()
					},
					isDisabled() {
						return !this.children.length
					},
				},
				'---',
				{
					label: 'Wrap with If',
					onClick(editor) {
						editor.wrapWithIf()
					},
				},
				{
					label: 'Wrap with Layout',
					onClick(editor) {
						editor.wrapWithLayout()
					},
				},
				'---',
				{
					label: 'Move before parent',
					onClick(editor) {
						editor.moveNextToParent('before', this)
					},
					isDisabled() {
						return !this.parent?.parent
					},
				},
				{
					label: 'Move after parent',
					onClick(editor) {
						editor.moveNextToParent('after', this)
					},
					isDisabled() {
						return !this.parent?.parent
					},
				},
				{
					label: 'Move into previous sibling',
					onClick(editor) {
						editor.moveIntoSibling('previous', this)
					},
					isDisabled() {
						if (!this.parent) return false
						const idxInParent = this.parent.children.findIndex(
							(b) => b === this,
						)
						const sibling = this.parent.children[idxInParent - 1]
						return !sibling || !sibling.canHaveChildren()
					},
				},
				{
					label: 'Move into next sibling',
					onClick(editor) {
						editor.moveIntoSibling('next', this)
					},
					isDisabled() {
						if (!this.parent) return false
						const idxInParent = this.parent.children.findIndex(
							(b) => b === this,
						)
						const sibling = this.parent.children[idxInParent + 1]
						return !sibling || !sibling.canHaveChildren()
					},
				},
			] as BlockMenuEntry[]
		).filter(Boolean)
	}

	getContentArea(name: string) {
		return this.children.find(
			(child) =>
				child.__type == 'ContentAreaBlock' &&
				(child as ContentAreaBlock).name == name,
		)
	}

	getAdditionalEditorProperties?(): EditorPropertyWithKey[] {
		return []
	}

	shouldRenderContentAreaWrappers() {
		return false
	}

	canHaveValidators() {
		return false
	}

	getValidationValueType(): string {
		return 'any'
	}

	canHaveEventHandlers() {
		return !!this.allowedEventTypes.length
	}

	getChildrenToCompile(): Block[] {
		return this.children
	}

	filterDeepClonedChildrenKeepingLogicBlocks(
		predicate: (block: Block) => boolean,
	): Block[] {
		const clonedChildren = clone(this.children)

		const filterChildren = (children: Block[]): Block[] => {
			return children.flatMap((child) => {
				if (child.isLogicBlock()) {
					child.children = filterChildren(child.children)
					if (!child.children.length) return []
					return [child]
				} else {
					return predicate(child) ? [child] : []
				}
			})
		}
		const filteredChildren = filterChildren(clonedChildren)

		return filteredChildren
	}

	isRequired() {
		return (
			this.canHaveValidators() &&
			!!this.validationRules?.some((vr) => vr.validationType == 'required')
		)
	}
}
